import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Plus, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackSearch = ({ onAddTrack, existingTrackIds = [], placeholder = "Search for songs..." }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const response = await axios.get(`${API}/music/search`, { params: { q: query, limit: 10 } });
      setResults(response.data.tracks || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') search();
  };

  const formatDuration = (ms) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div data-testid="track-search">
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            data-testid="track-search-input"
          />
        </div>
        <Button
          onClick={search}
          disabled={!query.trim() || searching}
          className="bg-[#FC3C44] hover:bg-[#E0353D] text-white"
          data-testid="track-search-button"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {results.map((track) => {
            const isAdded = existingTrackIds.includes(track.id);
            return (
              <div key={track.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 hover:bg-white/8 transition-colors" data-testid={`search-result-${track.id}`}>
                {track.album_image && (
                  <img src={track.album_image} alt={track.album} className="w-10 h-10 rounded object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{track.name}</p>
                  <p className="text-gray-400 text-xs truncate">
                    {Array.isArray(track.artists) ? track.artists.join(', ') : track.artists} · {track.album}
                  </p>
                </div>
                <span className="text-gray-400 text-xs">{formatDuration(track.duration_ms)}</span>
                <Button
                  size="sm"
                  onClick={() => onAddTrack(track)}
                  disabled={isAdded}
                  className={`h-8 px-3 ${isAdded ? 'bg-green-600/20 text-green-400' : 'bg-white/10 hover:bg-white/15 text-white'}`}
                  data-testid={`add-track-${track.id}`}
                >
                  {isAdded ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrackSearch;
